/**
 * Store locator filtering.
 *
 * The dropdowns are built from the dealer cards on the page, never from a list
 * kept here. Add a dealer in a new city and the city appears; remove the last
 * dealer in a state and the state disappears. There is no mapping to maintain
 * and no way for the filter to offer a combination that returns nothing.
 *
 * The page opens on results: the dealers whose type is listed in the section's
 * "Store types shown first" setting. The location filter sits behind a toggle;
 * once a state is chosen it narrows across every dealer, live.
 *
 * State and city are mirrored to the query string so a result can be shared or
 * bookmarked, and so the back button steps through filters rather than leaving
 * the page.
 *
 * The map is Leaflet over OpenStreetMap tiles, vendored into `assets/` and
 * injected once the list is on screen rather than as part of the page load.
 * Markers mirror the filtered list exactly, so the
 * two panes can never disagree about which dealers are showing.
 */

class StoreLocator extends HTMLElement {
  connectedCallback() {
    this.stateSelect = this.querySelector('[data-locator-state]');
    this.citySelect = this.querySelector('[data-locator-city]');
    this.toggle = this.querySelector('[data-locator-toggle]');
    this.heading = this.querySelector('[data-locator-title]');
    this.resetButton = this.querySelector('[data-locator-reset]');
    this.count = this.querySelector('[data-locator-count]');
    this.empty = this.querySelector('[data-locator-empty]');
    this.cards = Array.from(this.querySelectorAll('[data-dealer]'));
    this.sortCards();
    this.mapEl = this.querySelector('[data-locator-map]');

    if (!this.stateSelect || !this.citySelect || !this.cards.length) return;

    // Placeholders rather than an "All" option: the first row of each select
    // reads as an instruction until something is chosen. Its value is still
    // the empty string, so choosing it means "no filter" exactly as before.
    this.statePlaceholder = this.dataset.statePlaceholder || 'Select State';
    this.cityPlaceholder = this.dataset.cityPlaceholder || 'Select City';

    // The default view is every dealer ticked "Store listing by default". None
    // ticked means there is no default view to show, so the page falls back to
    // every dealer rather than to an empty list.
    this.featuredHeading = this.dataset.featuredHeading || '';
    this.hasFeatured = this.cards.some((card) => card.hasAttribute('data-default'));

    // Marks the element as script-driven. Every rule that hides something is
    // scoped to this class, so with JavaScript off the page stays a plain,
    // complete list rather than an empty search box.
    this.classList.add('locator--ready');

    // state -> Set of its cities.
    this.index = new Map();
    for (const card of this.cards) {
      const state = card.dataset.state || '';
      const city = card.dataset.city || '';
      if (!state) continue;
      if (!this.index.has(state)) this.index.set(state, new Set());
      if (city) this.index.get(state).add(city);
    }

    this.fill(this.stateSelect, [...this.index.keys()]);
    this.citySelect.disabled = true;

    this.stateSelect.addEventListener('change', () => this.onStateChange());
    this.citySelect.addEventListener('change', () => this.apply(true));

    if (this.toggle) {
      this.toggle.hidden = false;
      this.toggle.addEventListener('click', () => {
        const open = !this.classList.contains('is-filtering');
        this.setFiltering(open);
        if (open) this.stateSelect.focus();
      });
    }

    if (this.resetButton) {
      this.resetButton.hidden = false;
      this.resetButton.addEventListener('click', () => {
        this.stateSelect.value = '';
        this.fill(this.citySelect, []);
        this.citySelect.disabled = true;
        // Back to the opening view — the default store types, not every dealer.
        this.apply(true);
      });
    }

    // A shared or bookmarked URL arrives already filtered.
    this.readUrl();
    window.addEventListener('popstate', () => this.readUrl());

    // After the list is filled, so the cards never wait on the map.
    this.initMap();
  }

  /**
   * Ranked dealers first, lowest rank on top; then everyone else by state,
   * city and name. Metaobject entries have no order the admin controls, so
   * without this the list follows creation order and gets harder to scan with
   * every dealer added. Done here because Liquid cannot index back into a
   * metaobject list to render it sorted.
   */
  sortCards() {
    const grid = this.querySelector('[data-locator-grid]');
    if (!grid) return;
    const rank = (card) => {
      const value = parseInt(card.dataset.rank, 10);
      return Number.isFinite(value) ? value : Infinity;
    };
    const text = (card, key) => card.dataset[key] || '';
    this.cards.sort(
      (a, b) =>
        rank(a) - rank(b) ||
        text(a, 'state').localeCompare(text(b, 'state')) ||
        text(a, 'city').localeCompare(text(b, 'city')) ||
        text(a, 'name').localeCompare(text(b, 'name'))
    );
    grid.append(...this.cards);
  }

  /** Rebuilds a select as its placeholder plus the given values, sorted. */
  fill(select, values) {
    const placeholder =
      select === this.stateSelect ? this.statePlaceholder : this.cityPlaceholder;

    select.textContent = '';
    select.append(new Option(placeholder, ''));
    for (const value of values.sort((a, b) => a.localeCompare(b))) {
      select.append(new Option(value, value));
    }
  }

  /** Opens or closes the location filter. A class rather than `hidden`: the
   *  filter box sets `display: flex`, which beats the attribute. */
  setFiltering(open) {
    this.classList.toggle('is-filtering', open);
    if (this.toggle) this.toggle.setAttribute('aria-expanded', String(open));
  }

  onStateChange() {
    const state = this.stateSelect.value;
    this.fill(this.citySelect, state ? [...this.index.get(state)] : []);
    // With no state chosen there is nothing to narrow, so the city select is
    // disabled rather than shown holding only its placeholder.
    this.citySelect.disabled = !state;
    this.apply(true);
  }

  apply(push) {
    const state = this.stateSelect.value;
    const city = this.citySelect.value;
    let shown = 0;

    // No state chosen is the default view: only the listed store types.
    const featured = !state && this.hasFeatured;

    for (const card of this.cards) {
      const match = featured
        ? card.hasAttribute('data-default')
        : (!state || card.dataset.state === state) && (!city || card.dataset.city === city);
      // A class, not the `hidden` attribute: `.dealer-card` sets `display:
      // flex`, which beats the attribute's low-specificity UA rule and leaves
      // every card on screen. Anything given a `display` by a class needs a
      // class to hide it again.
      card.classList.toggle('is-filtered-out', !match);
      if (match) shown += 1;
    }

    if (this.empty) this.empty.hidden = shown > 0;

    if (this.count) {
      const template = shown === 1 ? this.count.dataset.one : this.count.dataset.other;
      this.count.textContent = template ? template.replace('[count]', shown) : String(shown);
    }

    if (this.heading) {
      this.heading.textContent = featured ? this.featuredHeading : city ? `${city}, ${state}` : state;
    }

    // The map mirrors the list rather than filtering independently, so the two
    // panes cannot disagree about which dealers are showing.
    this.syncMarkers();

    if (push) this.writeUrl(state, city);
  }

  writeUrl(state, city) {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    state ? params.set('state', state) : params.delete('state');
    city ? params.set('city', city) : params.delete('city');

    // The point is that the back button walks the filters — but an unchanged
    // URL should not stack up duplicate history entries either.
    if (url.href !== window.location.href) {
      window.history.pushState({}, '', url);
    }
  }


  /* ------------------------------------------------------------------ Map */

  /** Injects a stylesheet or script once, resolving when it has loaded. */
  static load(tag, attrs) {
    const selector = tag === 'script' ? `script[src="${attrs.src}"]` : `link[href="${attrs.href}"]`;
    // A second locator on the page, or a theme-editor re-render, must not
    // inject Leaflet twice — and must not give up either. If the tag is
    // already there, either it has loaded or we wait on the one in flight.
    const existing = document.querySelector(selector);
    if (existing) {
      if (existing.dataset.loaded) return Promise.resolve();
      return new Promise((resolve, reject) => {
        existing.addEventListener('load', resolve);
        existing.addEventListener('error', reject);
      });
    }

    return new Promise((resolve, reject) => {
      const el = Object.assign(document.createElement(tag), attrs);
      el.addEventListener('load', () => {
        el.dataset.loaded = 'true';
        resolve();
      });
      el.addEventListener('error', reject);
      document.head.append(el);
    });
  }

  async initMap() {
    const { leafletCss, leafletJs } = this.dataset;
    if (!this.mapEl || !leafletJs || this.map) return;

    try {
      await Promise.all([
        StoreLocator.load('link', { rel: 'stylesheet', href: leafletCss }),
        StoreLocator.load('script', { src: leafletJs }),
      ]);
    } catch {
      // The list is the source of truth and is already on screen; a map that
      // cannot load should cost the visitor nothing but the pane it sits in.
      this.mapEl.closest('.locator-map-pane')?.remove();
      return;
    }

    this.map = L.map(this.mapEl, {
      // A full-height map beside a scrolling list will swallow the page scroll
      // if the wheel zooms. Click to enable, which is the convention.
      scrollWheelZoom: false,
      zoomControl: true,
    });

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: Number(this.dataset.mapMaxZoom) || 19,
      attribution: this.dataset.mapAttribution || '© OpenStreetMap contributors',
    }).addTo(this.map);

    // Markers are built once and added or removed as the filter changes —
    // cheaper than recreating them, and it keeps popup state stable.
    this.markers = new Map();
    for (const card of this.cards) {
      const lat = parseFloat(card.dataset.lat);
      const lng = parseFloat(card.dataset.lng);
      if (Number.isNaN(lat) || Number.isNaN(lng)) continue;

      const marker = L.marker([lat, lng], { icon: StoreLocator.pin() }).bindPopup(
        StoreLocator.popup(card)
      );
      this.markers.set(card, marker);

      // Clicking a card moves the map to it, so the list stays the primary
      // way to navigate and the map follows.
      card.addEventListener('click', (event) => {
        if (event.target.closest('a')) return;
        this.map.setView([lat, lng], Math.max(this.map.getZoom(), 12));
        marker.openPopup();
      });
    }

    this.syncMarkers();
  }

  /** A brand-coloured pin, drawn rather than loaded — no image assets, and it
   *  recolours with the accent token like everything else. */
  static pin() {
    return L.divIcon({
      className: 'locator-pin',
      html:
        '<svg viewBox="0 0 24 32" width="28" height="37" aria-hidden="true">' +
        '<path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20c0-6.6-5.4-12-12-12z"/>' +
        '<circle cx="12" cy="12" r="4.5"/></svg>',
      iconSize: [28, 37],
      iconAnchor: [14, 37],
      popupAnchor: [0, -34],
    });
  }

  static popup(card) {
    const name = card.dataset.label || '';
    const sub = card.dataset.sub || '';
    const tel = card.dataset.tel || '';
    const esc = (v) => v.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);

    return (
      `<strong>${esc(name)}</strong>` +
      (sub ? `<br>${esc(sub)}` : '') +
      (tel ? `<br><a href="tel:${esc(tel)}">${esc(tel)}</a>` : '')
    );
  }

  /** Puts exactly the visible dealers on the map and frames them. */
  syncMarkers() {
    if (!this.map || !this.markers) return;

    const bounds = [];
    for (const [card, marker] of this.markers) {
      const visible = !card.classList.contains('is-filtered-out');
      if (visible) {
        marker.addTo(this.map);
        bounds.push(marker.getLatLng());
      } else {
        marker.remove();
      }
    }

    if (!bounds.length) return;

    // A single marker has no extent to fit, so `fitBounds` would zoom to the
    // maximum. Centre it at a readable street-level zoom instead.
    if (bounds.length === 1) {
      this.map.setView(bounds[0], 13);
    } else {
      this.map.fitBounds(L.latLngBounds(bounds), { padding: [40, 40] });
    }
  }

  readUrl() {
    const params = new URLSearchParams(window.location.search);
    const state = params.get('state') || '';
    const city = params.get('city') || '';

    // Guard both: the URL is user-editable, and a dealer may have been removed
    // since the link was shared.
    this.stateSelect.value = this.index.has(state) ? state : '';

    const cities = this.stateSelect.value ? [...this.index.get(this.stateSelect.value)] : [];
    this.fill(this.citySelect, cities);
    this.citySelect.disabled = !this.stateSelect.value;
    this.citySelect.value = cities.includes(city) ? city : '';

    // A URL carrying a filter opens with the filter showing, so the visitor can
    // see why the list is narrowed. A bare /pages/store-locator opens on the
    // default view with the filter closed.
    if (this.stateSelect.value) this.setFiltering(true);
    this.apply(false);
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
